/**
 * ShiftCheckIn - GPS Check-in/Check-out component for medics
 * Handles geolocation and displays check-in status
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  MapPin, 
  Navigation, 
  Loader2, 
  CheckCircle, 
  XCircle,
  AlertTriangle,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { trackingAPI } from '../../lib/api';
import { format } from 'date-fns';

export default function ShiftCheckIn({ shift, onStatusChange }) {
  const [loading, setLoading] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  
  // Check-in status from shift data
  const isCheckedIn = !!shift?.checked_in_at;
  const isCheckedOut = !!shift?.checked_out_at;
  const canCheckIn = !isCheckedIn && !isCheckedOut && shift?.status !== 'cancelled' && shift?.status !== 'completed';
  const canCheckOut = isCheckedIn && !isCheckedOut;

  // Get current location
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }
      
      setGettingLocation(true);
      setLocationError(null);
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          setCurrentLocation(loc);
          setGettingLocation(false);
          resolve(loc);
        },
        (error) => {
          let errorMsg = 'Failed to get location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMsg = 'Location permission denied. Please enable location access in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMsg = 'Location information is unavailable.';
              break;
            case error.TIMEOUT:
              errorMsg = 'Location request timed out.';
              break;
            default:
              errorMsg = 'An unknown error occurred while getting location.';
          }
          setLocationError(errorMsg);
          setGettingLocation(false);
          reject(new Error(errorMsg));
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0
        }
      );
    });
  };

  // Handle check-in
  const handleCheckIn = async () => {
    setLoading(true);
    try {
      const location = await getCurrentLocation();
      
      const response = await trackingAPI.checkIn(shift.id, {
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy
      });
      
      toast.success('Successfully checked in!');
      
      if (response.data.distance_from_shift) {
        toast.info(`You are ${response.data.distance_from_shift}m from the shift location`);
      }
      
      // Notify parent to refresh shift data
      if (onStatusChange) {
        onStatusChange();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to check in';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Handle check-out
  const handleCheckOut = async () => {
    setLoading(true);
    try {
      const location = await getCurrentLocation();
      
      await trackingAPI.checkOut(shift.id, {
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy
      });
      
      toast.success('Successfully checked out!');
      
      // Notify parent to refresh shift data
      if (onStatusChange) {
        onStatusChange();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to check out';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Don't show for cancelled/completed shifts without check-in
  if (shift?.status === 'cancelled' || (shift?.status === 'completed' && !isCheckedIn)) {
    return null;
  }

  return (
    <Card className="bg-[#2B2B2B] border-white/5" data-testid="shift-checkin-card">
      <CardHeader>
        <CardTitle className="text-[#F2F2F2] font-['Manrope'] flex items-center gap-2">
          <Navigation className="w-5 h-5 text-[#1ABC9C]" />
          GPS Check-in
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status Display */}
        <div className="p-4 bg-[#1A1A1A] rounded-lg border border-white/5">
          {isCheckedOut ? (
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-gray-500/20">
                <CheckCircle className="w-5 h-5 text-gray-400" />
              </div>
              <div>
                <p className="text-[#F2F2F2] font-medium">Shift Completed</p>
                <p className="text-xs text-[#A1A1AA]">
                  Checked in: {format(new Date(shift.checked_in_at), 'HH:mm')} | 
                  Checked out: {format(new Date(shift.checked_out_at), 'HH:mm')}
                </p>
              </div>
            </div>
          ) : isCheckedIn ? (
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-[#1ABC9C]/20">
                <CheckCircle className="w-5 h-5 text-[#1ABC9C]" />
              </div>
              <div>
                <p className="text-[#1ABC9C] font-medium">Currently Checked In</p>
                <p className="text-xs text-[#A1A1AA]">
                  Since {format(new Date(shift.checked_in_at), 'HH:mm')}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-[#FFC04D]/20">
                <Clock className="w-5 h-5 text-[#FFC04D]" />
              </div>
              <div>
                <p className="text-[#FFC04D] font-medium">Not Checked In</p>
                <p className="text-xs text-[#A1A1AA]">
                  Check in when you arrive at the shift location
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Location Error Alert */}
        {locationError && (
          <Alert className="bg-red-500/10 border-red-500/20">
            <AlertTriangle className="h-4 w-4 text-red-400" />
            <AlertDescription className="text-red-400 text-sm">
              {locationError}
            </AlertDescription>
          </Alert>
        )}

        {/* Current Location Display */}
        {currentLocation && (
          <div className="p-3 bg-[#1A1A1A] rounded-lg border border-white/5">
            <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
              <MapPin className="w-4 h-4" />
              <span>
                Location: {currentLocation.latitude.toFixed(6)}, {currentLocation.longitude.toFixed(6)}
              </span>
              {currentLocation.accuracy && (
                <Badge className="bg-white/10 text-[#A1A1AA] border-0 text-xs ml-auto">
                  ±{Math.round(currentLocation.accuracy)}m
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          {canCheckIn && (
            <Button
              onClick={handleCheckIn}
              disabled={loading || gettingLocation}
              className="flex-1 bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
              data-testid="check-in-btn"
            >
              {loading || gettingLocation ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <MapPin className="w-4 h-4 mr-2" />
              )}
              {gettingLocation ? 'Getting Location...' : 'Check In'}
            </Button>
          )}
          
          {canCheckOut && (
            <Button
              onClick={handleCheckOut}
              disabled={loading || gettingLocation}
              variant="outline"
              className="flex-1 border-[#FFC04D]/30 text-[#FFC04D] hover:bg-[#FFC04D]/10"
              data-testid="check-out-btn"
            >
              {loading || gettingLocation ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              {gettingLocation ? 'Getting Location...' : 'Check Out'}
            </Button>
          )}
        </div>

        {/* Help Text */}
        {(canCheckIn || canCheckOut) && (
          <p className="text-xs text-[#A1A1AA] text-center">
            Your browser will request location permission to verify your position.
          </p>
        )}

        {/* Shift Location Coordinates Info */}
        {shift?.location_lat && shift?.location_lng && (
          <div className="text-xs text-[#A1A1AA] border-t border-white/5 pt-3 mt-3">
            <span className="flex items-center gap-1">
              <Navigation className="w-3 h-3" />
              Geofenced location - check-in requires being near the shift site
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
